import type { AIStep, AIWorkoutResponse } from '@/types/workout';

function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null && !Array.isArray(x);
}

/** Model contract: refuse jailbreak / off-topic with this payload only. */
export function isPromptUnsafeResponse(output: unknown): boolean {
  if (!isRecord(output)) return false;
  return output.error === 'PROMPT_UNSAFE';
}

function isAiStepLike(x: unknown): boolean {
  if (!isRecord(x)) return false;
  const w = x.work;
  if (!isRecord(w)) return false;
  return (
    typeof x.type === 'string' &&
    typeof w.duration === 'number' &&
    Number.isFinite(w.duration) &&
    w.duration > 0 &&
    typeof w.unit === 'string' &&
    typeof w.intensity === 'string' &&
    w.intensity.trim().length > 0
  );
}

/** Sum work.duration for steps in minutes (common LLM output) — fallback when root duration missing. */
function inferredDurationMinutes(steps: unknown[]): number {
  let sum = 0;
  for (const s of steps) {
    if (!isRecord(s)) continue;
    const w = s.work;
    if (!isRecord(w) || typeof w.duration !== 'number' || !Number.isFinite(w.duration)) continue;
    const u = w.unit;
    if (u === 'min') sum += w.duration * (typeof s.repeats === 'number' && s.repeats > 1 ? s.repeats : 1);
    else if (u === 'sec')
      sum += (w.duration * (typeof s.repeats === 'number' && s.repeats > 1 ? s.repeats : 1)) / 60;
    else if (u === 'km' || u === 'm') sum += w.duration / 10;
  }
  return sum;
}

function hasTargetPowerSignal(o: Record<string, unknown>, steps: unknown[]): boolean {
  const tp = o.target_power ?? o.targetPower ?? o.ftp ?? o.functionalThresholdPower;
  if (typeof tp === 'number' && Number.isFinite(tp) && tp > 0) return true;
  if (typeof tp === 'string' && /%|ftp|w\b|wat/i.test(tp)) return true;

  for (const s of steps) {
    if (!isRecord(s)) continue;
    const w = s.work;
    if (!isRecord(w)) continue;
    const inten = w.intensity;
    if (
      typeof inten === 'string' &&
      /%|ftp|threshold|z\d|vo2|sweet|ss\b|pace|hr\b|steady|endurance|warm|cool|recovery|tempo|interval/i.test(
        inten,
      )
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Semantic JSON contract for orchestrator health (aliases + minimal step shape).
 * Aligns with production `AIWorkoutResponse` while accepting `workout_name` / `intervals` / `duration`.
 */
export function validateWorkoutJSON(output: unknown): boolean {
  if (!isRecord(output)) return false;
  if (isPromptUnsafeResponse(output)) return false;

  const name = output.workout_name ?? output.workoutName;
  if (typeof name !== 'string' || name.trim().length === 0) return false;

  const intervals = output.intervals ?? output.steps;
  if (!Array.isArray(intervals) || intervals.length === 0) return false;
  if (!intervals.every(isAiStepLike)) return false;

  const rawDur = output.duration ?? output.totalDuration ?? output.total_duration;
  const hasRootDuration =
    typeof rawDur === 'number' && Number.isFinite(rawDur) && (rawDur as number) > 0;
  const inferred = inferredDurationMinutes(intervals);
  if (!hasRootDuration && inferred <= 0) return false;

  if (!hasTargetPowerSignal(output, intervals)) return false;

  return true;
}

export function normalizeParsedWorkout(parsed: unknown): AIWorkoutResponse {
  if (!isRecord(parsed)) throw new Error('normalizeParsedWorkout: expected object');
  const workoutName = String(parsed.workoutName ?? parsed.workout_name ?? '').trim();
  const rawSteps = (parsed.steps ?? parsed.intervals) as unknown[];
  if (!Array.isArray(rawSteps)) throw new Error('normalizeParsedWorkout: missing steps/intervals');
  const steps = rawSteps as AIStep[];

  let totalDuration: number | undefined;
  const td = parsed.totalDuration ?? parsed.total_duration ?? parsed.duration;
  if (typeof td === 'number' && Number.isFinite(td) && td > 0) {
    totalDuration = td;
  } else {
    const inf = inferredDurationMinutes(rawSteps);
    if (inf > 0) totalDuration = Math.round(inf);
  }

  return { workoutName, steps, totalDuration };
}
